import { isNil } from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment-timezone';
import * as React from 'react';
import { CircularProgress, Modal } from '@mui/material';
import FontAwesome from 'react-fontawesome';
import { useDispatch } from 'react-redux';
import { getReservations } from './Api';
import { Reservation, ReservationStatus } from './types';
import * as Api from './Api';
import { enqueueNotification } from './actions/Actions';
// import { BASE_URL } from './Constants';

const STATUS_CLASS_MAP: { [key in ReservationStatus]: string } = {
    [ReservationStatus.USER_VERIFIED]: 'user-verified',
    [ReservationStatus.STORE_CONFIRMED]: 'store-confirmed',
    [ReservationStatus.RESERVATION_NOT_SHOWN]: '',
    [ReservationStatus.RESERVATION_SHOWN]: '',
    [ReservationStatus.USER_NOT_VERIFIED]: 'user-not-verified'
};

const Reservations: React.FC = () => {
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = React.useState(moment());
    const [reservations, setReservations] = React.useState<Reservation[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showModal, setShowModal] = React.useState(false);
    const [selectedReservationId, setSelectedReservationId] = React.useState<number>();

    const handleDateChange = React.useCallback((date: moment.Moment) => {
        setSelectedDate(date);
    }, []);

    const openReservationModal = React.useCallback(
        (id: number) => () => {
            setSelectedReservationId(id);
            setShowModal(true);
        },
        []
    );

    const onVerify = React.useCallback(
        (id: number) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();

            Api.confirmReservation(id)
                .then(status => {
                    setReservations(
                        reservations.map(res =>
                            res.id === id ? { ...res, status: Number(status) } : res
                        )
                    );
                })
                .catch((err: Error) => {
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: err.message,
                            options: { variant: 'error' }
                        })
                    );
                });
        },
        [dispatch, reservations]
    );

    const onTest = React.useCallback(
        (id: number) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            console.log(id);
        },
        []
    );

    const closeModal = React.useCallback(() => {
        setShowModal(false);
        setSelectedReservationId(null);
    }, []);

    const getStatusActionButtons = React.useCallback(
        (id: number, status: ReservationStatus) => {
            switch (status) {
                case ReservationStatus.USER_VERIFIED:
                    return (
                        <div className='store-confirm-actions'>
                            <button
                                type='button'
                                className='verify'
                                onClick={onVerify(id)}>
                                VERIFY
                            </button>
                        </div>
                    );
                case ReservationStatus.STORE_CONFIRMED:
                    return (
                        <div className='store-confirm-actions'>
                            <span
                                className='fa-stack fa-lg confirm'
                                onClick={onTest(id)}>
                                <FontAwesome name='circle-thin' className='circle' />
                                <FontAwesome name='check' className='checkmark' />
                            </span>
                            <span
                                className='fa-stack fa-lg cancel'
                                onClick={onTest(id)}>
                                <FontAwesome name='circle-thin' className='circle' />
                                <FontAwesome name='times' className='times' />
                            </span>
                        </div>
                    );
                default:
                    return null;
            }
        },
        [onVerify, onTest]
    );

    const renderReservations = React.useCallback(() => {
        if (loading) {
            return <CircularProgress className='spinner' />;
        }

        if (!reservations.length) {
            return 'Δεν υπάρχουν κρατήσεις';
        }

        return (
            <div className='reservation-items'>
                {reservations
                    .sort(
                        (a, b) =>
                            new Date(a.date).getTime() - new Date(b.date).getTime()
                    )
                    .map(
                        ({
                            status,
                            numberOfPersons,
                            comment,
                            date,
                            email,
                            id,
                            name,
                            phoneNumber
                        }) => (
                            <div key={id} className='reservation-wrapper' data-id={id}>
                                <div
                                    className={`reservation ${STATUS_CLASS_MAP[status]}`}
                                    onClick={openReservationModal(id)}>
                                    <div className='left'>
                                        <div className='top'>
                                            <div className='time'>
                                                {moment(date).format('HH:mm')}
                                            </div>
                                            <div className='people'>
                                                {numberOfPersons}
                                            </div>
                                            <div className='name'>{name}</div>
                                            <div className='phone'>{phoneNumber}</div>
                                            <div className='email'>{email}</div>
                                        </div>
                                        <div className='bottom'>
                                            Comments:
                                            <div className='comment'>
                                                {comment || '-'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='right actions'>
                                        {getStatusActionButtons(id, status)}
                                    </div>
                                </div>
                            </div>
                        )
                    )}
            </div>
        );
    }, [getStatusActionButtons, loading, openReservationModal, reservations]);

    const renderSelectedReservation = React.useCallback(() => {
        const { name, date, numberOfPersons, email, phoneNumber, comment, status } =
            reservations.find(({ id }) => id === selectedReservationId);

        return (
            <div className='container reservation-modal'>
                <div className='left'>
                    <div>
                        <div className='label'>Όνομα:</div>
                        {name}
                    </div>
                    <div>
                        <div className='label'>Ημερομηνία:</div>
                        {moment(date).format('DD/MM/YYYY - HH:mm')}
                    </div>
                    <div>
                        <div className='label'>Άτομα:</div>
                        {numberOfPersons}
                    </div>
                    <div>
                        <div className='label'>Στοιχεία επικοινωνίας:</div>
                        {[email, phoneNumber].join(', ')}
                    </div>
                    <div className='comments'>
                        <div className='label'>Σχόλια:</div>
                        {comment || '-'}
                    </div>
                    <div className='status'>
                        <div className='label'>Status:</div>
                        {ReservationStatus[status]}
                    </div>
                </div>
                <div className='right'>
                    <div>
                        <FontAwesome
                            name='times-circle'
                            className='close-icon'
                            onClick={closeModal}
                        />
                    </div>
                    {getStatusActionButtons(selectedReservationId, status)}
                </div>
            </div>
        );
    }, [closeModal, getStatusActionButtons, reservations, selectedReservationId]);

    const fetchAndSetReservations = React.useCallback(() => {
        setLoading(true);

        // fetch(`${BASE_URL}/Reservations/AddNew`, {
        //     method: 'POST',
        //     body: JSON.stringify({
        //         email: 'xe2niasi@gmail.com',
        //         numberOfPersons: 2,
        //         name: 'Xenia Siskaki',
        //         comment: 'Birthday party',
        //         phoneNumber: '+436781219657',
        //         date: '2023-10-07T15:00:00'
        //     }),
        //     headers: {
        //         Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6ImZzIiwiU3RTSUQiOiJlbV83TEVaZFpVQ0c2VlVKa3pSMTdnIiwiVGJsU0lEIjoiQUFBQUFBQUFBQUFBQUFBQUFBQUFBQSIsInJvbGUiOiJPd25lciIsIm5iZiI6MTY5NzM3MTcwMiwiZXhwIjoxNjk3Mzc1MzAyLCJpYXQiOjE2OTczNzE3MDIsImlzcyI6Imh0dHBzOi8vYmFja2JvbmVkZXYuc2NhbmFuZG9yZGVyLmV1LyIsImF1ZCI6Imh0dHBzOi8vYmFja2JvbmVkZXYuc2NhbmFuZG9yZGVyLmV1L2F1ZGllbmNlIn0.txo9W5kKssiFTauSs0NccNUGKCKbizOnN8a9V13HFJI`,
        //         'Content-Type': 'application/json'
        //     }
        // });

        getReservations(
            selectedDate.clone().startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        )
            .then(r => {
                setReservations(r);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [selectedDate]);

    React.useEffect(() => {
        fetchAndSetReservations();
    }, [fetchAndSetReservations, selectedDate]);

    return (
        <div className='reservations-wrapper'>
            <div className='date-selection'>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        className='date-picker'
                        slotProps={{ textField: { size: 'small' } }}
                        defaultValue={selectedDate}
                        onChange={handleDateChange}
                    />
                </LocalizationProvider>
                <FontAwesome
                    name='refresh'
                    className='refresh-reservations'
                    title='Ανανέωση'
                    onClick={fetchAndSetReservations}
                />
                <span className='fa-stack fa-lg new-reservation'>
                    <FontAwesome name='circle-thin' className='circle' />
                    <FontAwesome name='plus' className='add' />
                </span>
            </div>
            <div className='reservations'>{renderReservations()}</div>
            {!isNil(selectedReservationId) && (
                <Modal
                    open={showModal}
                    onClose={closeModal}
                    BackdropProps={{ classes: { root: 'backdrop' } }}
                    classes={{ root: 'modal-dialog' }}>
                    {renderSelectedReservation()}
                </Modal>
            )}
        </div>
    );
};

export default Reservations;
